import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical } from 'lucide-react';

export const FieldPicker: React.FC<{
    label: string,
    value: string | undefined,
    onChange: (val: string) => void,
    options: { label: string, value: string, isNumeric?: boolean, isConnection?: boolean }[],
    disabled?: boolean,
    allowNone?: boolean
}> = ({ label, value, onChange, options, disabled, allowNone = false }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value);
    const connectionFields = options.filter(o => o.isConnection);
    const hasConnections = connectionFields.length > 0;

    return (
        <div className="space-y-1.5 relative" ref={ref}>
            <label className="text-sm font-semibold text-gray-400">
                {label}
                {hasConnections && <span className="ml-2 text-xs text-emerald-400">↔ Connection fields listed first</span>}
            </label>
            <button
                onClick={() => !disabled && setOpen(!open)}
                disabled={disabled}
                className={`w-full text-left px-2 py-1.5 text-sm border rounded-lg bg-obsidian-800 flex items-center justify-between transition-all ${open ? 'ring-2 ring-gold-500/50 border-gold-500/50' : 'border-gold-500/20 hover:border-gold-500/40'} ${disabled ? 'bg-white/5 cursor-not-allowed opacity-60' : ''}`}
            >
                <span className={`block truncate text-sm ${!selectedOption ? 'text-gray-400' : 'text-gray-200'}`}>
                    {selectedOption ? (
                        <span className="flex items-center gap-2">
                            {selectedOption.isConnection && <span className="text-emerald-600">↔</span>}
                            {selectedOption.isNumeric ? <span className="text-xs font-mono bg-blue-100 text-blue-700 px-1 rounded">#</span> : <span className="text-xs font-mono bg-gray-100 text-gray-600 px-1 rounded">T</span>}
                            {selectedOption.label}
                        </span>
                    ) : (allowNone ? 'None' : 'Select Field')}
                </span>
                <MoreVertical className="h-3.5 w-3.5 text-gray-400 rotate-90" />
            </button>

            {open && (
                <div className="absolute z-50 w-full mt-1 bg-obsidian-900 rounded-lg shadow-xl border border-gold-500/20 max-h-60 overflow-y-auto">
                    {options.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">No fields available</div>
                    ) : (
                        <div className="py-1">
                            {allowNone && (
                                <button
                                    onClick={() => { onChange(''); setOpen(false); }}
                                    className={`w-full text-left px-2 py-1.5 text-sm hover:bg-gold-500/20 flex items-center gap-2 ${!value ? 'bg-gold-500/30 text-gold-400 font-bold' : 'text-gray-400'}`}
                                >
                                    <span className="italic">None</span>
                                </button>
                            )}
                            {options.map((opt, idx) => (
                                <button
                                    key={opt.value}
                                    onClick={() => { onChange(opt.value); setOpen(false); }}
                                    className={`w-full text-left px-2 py-1.5 text-sm hover:bg-gold-500/20 flex items-center gap-2 ${opt.value === value ? 'bg-gold-500/30 text-gold-400 font-bold' : 'text-gray-300'} ${opt.isConnection ? 'bg-emerald-50/30' : ''}`}
                                >
                                    {opt.isConnection && <span className="text-emerald-600 text-xs">↔</span>}
                                    {opt.isNumeric ? <span className="text-xs font-mono bg-blue-100 text-blue-700 px-1 rounded w-5 text-center">#</span> : <span className="text-xs font-mono bg-gray-100 text-gray-600 px-1 rounded w-5 text-center">T</span>}
                                    <span className={opt.value === value ? 'font-bold' : ''}>{opt.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
